import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ToastProvider } from './context/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import PermissionProtectedRoute from './components/PermissionProtectedRoute';
import Layout from './components/Layout';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';

// Portal
import Portal from './pages/portal/Portal';

// HRIS Pages
import HRISDashboard from './pages/hris/HRISDashboard';
import EmployeeList from './pages/hris/EmployeeList';
import EmployeeForm from './pages/hris/EmployeeForm';
import EmployeeDetail from './pages/hris/EmployeeDetail';
import OrganizationStructure from './pages/hris/OrganizationStructure';
import PunishmentList from './pages/hris/PunishmentList';
import PunishmentForm from './pages/hris/PunishmentForm';
import EmployeeDevelopmentList from './pages/hris/EmployeeDevelopmentList';
import EmployeeDevelopmentForm from './pages/hris/EmployeeDevelopmentForm';
import EmployeeDevelopmentDetail from './pages/hris/EmployeeDevelopmentDetail';
import RecruitmentList from './pages/hris/RecruitmentList';
import AttendanceList from './pages/hris/AttendanceList';
import LeaveList from './pages/hris/LeaveList';
import OvertimeList from './pages/hris/OvertimeList';
import KPIList from './pages/hris/KPIList';
import PayrollList from './pages/hris/PayrollList';
import RoleAccessManagement from './pages/hris/RoleAccessManagement';
import RoleAccessDetail from './pages/hris/RoleAccessDetail';

// Debtor Pages
import DebtorDashboard from './pages/debtor/DebtorDashboard';
import DebtorList from './pages/debtor/DebtorList';
import DebtorForm from './pages/debtor/DebtorForm';
import DebtorDetail from './pages/debtor/DebtorDetail';
import DocumentSearch from './pages/debtor/DocumentSearch';
import DocumentValidation from './pages/debtor/DocumentValidation';

// Credit Pages
import CreditDashboard from './pages/credit/CreditDashboard';
import CreditSubmissionForm from './pages/credit/CreditSubmissionForm';
import CreditSubmissionDetail from './pages/credit/CreditSubmissionDetail';
import CreditApplicationList from './pages/credit/CreditApplicationList';
import ScoringModelForm from './pages/credit/ScoringModelForm';
import ScoringModelList from './pages/credit/ScoringModelList';
import ApprovalList from './pages/credit/ApprovalList';

// Archive Pages
import ArchiveDashboard from './pages/archive/ArchiveDashboard';
import ArchiveCategoryList from './pages/archive/ArchiveCategoryList';
import DocumentTypeList from './pages/archive/DocumentTypeList';
import DocumentList from './pages/archive/DocumentList';
import DocumentValidationWorkflow from './pages/archive/DocumentValidationWorkflow';

// Profile Page
import Profile from './pages/Profile';
import Settings from './pages/Settings';

import './App.css';

function App(): React.ReactElement {
  return (
    <LanguageProvider>
      <AuthProvider>
        <ToastProvider>
          <Router>
            <Routes>
              {/* Auth Routes - Public */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />

              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout currentPageName="Portal">
                      <Portal />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/portal"
                element={
                  <ProtectedRoute>
                    <Layout currentPageName="Portal">
                      <Portal />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrisdashboard"
                element={
                  <ProtectedRoute>
                    <Layout currentPageName="HRISDashboard">
                      <HRISDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employeelist"
                element={
                  <PermissionProtectedRoute permission="hris_emp.employee.view">
                    <Layout currentPageName="EmployeeList">
                      <EmployeeList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/employeeform"
                element={
                  <PermissionProtectedRoute
                    permissions={['hris_emp.employee.create', 'hris_emp.employee.update']}
                    fallbackMessage="Anda tidak memiliki akses untuk mengelola data pegawai. Hubungi administrator untuk mendapatkan akses."
                  >
                    <Layout currentPageName="EmployeeForm">
                      <EmployeeForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/hris/employees"
                element={
                  <PermissionProtectedRoute permission="hris_emp.employee.view">
                    <Layout currentPageName="EmployeeList">
                      <EmployeeList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/hris/employees/new"
                element={
                  <PermissionProtectedRoute permission="hris_emp.employee.create">
                    <Layout currentPageName="EmployeeForm">
                      <EmployeeForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/hris/employees/edit"
                element={
                  <PermissionProtectedRoute permission="hris_emp.employee.update">
                    <Layout currentPageName="EmployeeForm">
                      <EmployeeForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/employeedetail"
                element={
                  <ProtectedRoute>
                    <Layout currentPageName="EmployeeDetail">
                      <EmployeeDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hris/employees/detail"
                element={
                  <ProtectedRoute>
                    <Layout currentPageName="EmployeeDetail">
                      <EmployeeDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organizationstructure"
                element={
                  <PermissionProtectedRoute permission="hris_org.organization.view">
                    <Layout currentPageName="OrganizationStructure">
                      <OrganizationStructure />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/punishmentlist"
                element={
                  <PermissionProtectedRoute permission="hris_punish.punishment.view">
                    <Layout currentPageName="PunishmentList">
                      <PunishmentList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/punishmentform"
                element={
                  <PermissionProtectedRoute
                    permissions={['hris_punish.punishment.create', 'hris_punish.punishment.update']}
                    fallbackMessage="Anda tidak memiliki akses untuk mengelola data hukuman. Hubungi administrator untuk mendapatkan akses."
                  >
                    <Layout currentPageName="PunishmentForm">
                      <PunishmentForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/hris/punishments/new"
                element={
                  <PermissionProtectedRoute permission="hris_punish.punishment.create">
                    <Layout currentPageName="PunishmentForm">
                      <PunishmentForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/hris/punishments/edit"
                element={
                  <PermissionProtectedRoute permission="hris_punish.punishment.update">
                    <Layout currentPageName="PunishmentForm">
                      <PunishmentForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/employeedevelopmentlist"
                element={
                  <PermissionProtectedRoute permission="hris_dev.development.view">
                    <Layout currentPageName="EmployeeDevelopmentList">
                      <EmployeeDevelopmentList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/employeedevelopmentform"
                element={
                  <PermissionProtectedRoute
                    permissions={['hris_dev.development.create', 'hris_dev.development.update']}
                    fallbackMessage="Anda tidak memiliki akses untuk mengelola data pengembangan pegawai. Hubungi administrator untuk mendapatkan akses."
                  >
                    <Layout currentPageName="EmployeeDevelopmentForm">
                      <EmployeeDevelopmentForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/hris/development/new"
                element={
                  <PermissionProtectedRoute permission="hris_dev.development.create">
                    <Layout currentPageName="EmployeeDevelopmentForm">
                      <EmployeeDevelopmentForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/hris/development/edit"
                element={
                  <PermissionProtectedRoute permission="hris_dev.development.update">
                    <Layout currentPageName="EmployeeDevelopmentForm">
                      <EmployeeDevelopmentForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/employeedevelopmentdetail"
                element={
                  <PermissionProtectedRoute permission="hris_dev.development.view">
                    <Layout currentPageName="EmployeeDevelopmentDetail">
                      <EmployeeDevelopmentDetail />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/hris/development/detail"
                element={
                  <PermissionProtectedRoute permission="hris_dev.development.view">
                    <Layout currentPageName="EmployeeDevelopmentDetail">
                      <EmployeeDevelopmentDetail />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/recruitmentlist"
                element={
                  <PermissionProtectedRoute permission="hris_recruit.recruitment.view">
                    <Layout currentPageName="RecruitmentList">
                      <RecruitmentList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/attendancelist"
                element={
                  <PermissionProtectedRoute permission="hris_att.attendance.view">
                    <Layout currentPageName="AttendanceList">
                      <AttendanceList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/overtimelist"
                element={
                  <PermissionProtectedRoute permission="hris_att.attendance.view">
                    <Layout currentPageName="OvertimeList">
                      <OvertimeList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/leavelist"
                element={
                  <PermissionProtectedRoute permission="hris_leave.leave.view">
                    <Layout currentPageName="LeaveList">
                      <LeaveList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/kpilist"
                element={
                  <PermissionProtectedRoute permission="hris_kpi.kpi.view">
                    <Layout currentPageName="KPIList">
                      <KPIList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/payrolllist"
                element={
                  <PermissionProtectedRoute permission="hris_payroll.payroll.view">
                    <Layout currentPageName="PayrollList">
                      <PayrollList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/roleaccessmanagement"
                element={
                  <PermissionProtectedRoute permission="hris_role_access.role_access.view">
                    <Layout currentPageName="RoleAccessManagement">
                      <RoleAccessManagement />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/roleaccessdetail"
                element={
                  <PermissionProtectedRoute permission="hris_role_access.role_access.view_detail">
                    <Layout currentPageName="RoleAccessDetail">
                      <RoleAccessDetail />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />

              {/* Debtor Routes */}
              <Route
                path="/debtordashboard"
                element={
                  <ProtectedRoute>
                    <Layout currentPageName="DebtorDashboard">
                      <DebtorDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/debtorlist"
                element={
                  <PermissionProtectedRoute permission="debtor.debtor.view">
                    <Layout currentPageName="DebtorList">
                      <DebtorList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/debtorform"
                element={
                  <PermissionProtectedRoute
                    permissions={['debtor.debtor.create', 'debtor.debtor.update']}
                    fallbackMessage="Anda tidak memiliki akses untuk mengelola data debitur. Hubungi administrator untuk mendapatkan akses."
                  >
                    <Layout currentPageName="DebtorForm">
                      <DebtorForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/debtor/new"
                element={
                  <PermissionProtectedRoute permission="debtor.debtor.create">
                    <Layout currentPageName="DebtorForm">
                      <DebtorForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/debtor/edit"
                element={
                  <PermissionProtectedRoute permission="debtor.debtor.update">
                    <Layout currentPageName="DebtorForm">
                      <DebtorForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/debtordetail"
                element={
                  <PermissionProtectedRoute permission="debtor.debtor.view_detail">
                    <Layout currentPageName="DebtorDetail">
                      <DebtorDetail />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/debtor/detail"
                element={
                  <PermissionProtectedRoute permission="debtor.debtor.view_detail">
                    <Layout currentPageName="DebtorDetail">
                      <DebtorDetail />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/documentsearch"
                element={
                  <PermissionProtectedRoute permission="debtor.debtor.search">
                    <Layout currentPageName="DocumentSearch">
                      <DocumentSearch />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/documentvalidation"
                element={
                  <PermissionProtectedRoute permission="debtor.validation.view">
                    <Layout currentPageName="DocumentValidation">
                      <DocumentValidation />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />

              {/* Credit Routes */}
              <Route
                path="/creditdashboard"
                element={
                  <ProtectedRoute>
                    <Layout currentPageName="CreditDashboard">
                      <CreditDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/credit/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout currentPageName="CreditDashboard">
                      <CreditDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/creditsubmissionform"
                element={
                  <PermissionProtectedRoute
                    permissions={['credit.submission.create', 'credit.submission.update']}
                    fallbackMessage="Anda tidak memiliki akses untuk mengelola pengajuan kredit. Hubungi administrator untuk mendapatkan akses."
                  >
                    <Layout currentPageName="CreditSubmissionForm">
                      <CreditSubmissionForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/credit/new"
                element={
                  <PermissionProtectedRoute permission="credit.submission.create">
                    <Layout currentPageName="CreditSubmissionForm">
                      <CreditSubmissionForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/scoringmodellist"
                element={
                  <PermissionProtectedRoute permission="credit.scoring.view">
                    <Layout currentPageName="ScoringModelList">
                      <ScoringModelList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/credit/scoring"
                element={
                  <PermissionProtectedRoute permission="credit.scoring.view">
                    <Layout currentPageName="ScoringModelList">
                      <ScoringModelList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/scoringmodelform"
                element={
                  <PermissionProtectedRoute
                    permissions={['credit.scoring.create', 'credit.scoring.update']}
                    fallbackMessage="Anda tidak memiliki akses untuk mengelola model scoring. Hubungi administrator untuk mendapatkan akses."
                  >
                    <Layout currentPageName="ScoringModelForm">
                      <ScoringModelForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/credit/scoring/new"
                element={
                  <PermissionProtectedRoute permission="credit.scoring.create">
                    <Layout currentPageName="ScoringModelForm">
                      <ScoringModelForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/credit/scoring/edit"
                element={
                  <PermissionProtectedRoute permission="credit.scoring.update">
                    <Layout currentPageName="ScoringModelForm">
                      <ScoringModelForm />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/approvallist"
                element={
                  <PermissionProtectedRoute permission="credit.approval.view">
                    <Layout currentPageName="ApprovalList">
                      <ApprovalList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/credit/approval"
                element={
                  <PermissionProtectedRoute permission="credit.approval.view">
                    <Layout currentPageName="ApprovalList">
                      <ApprovalList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/credit/application/detail"
                element={
                  <PermissionProtectedRoute permission="credit.application.view">
                    <CreditSubmissionDetail />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/credit/applications"
                element={
                  <PermissionProtectedRoute permission="credit.application.view">
                    <Layout currentPageName="CreditApplicationList">
                      <CreditApplicationList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/creditapplicationlist"
                element={
                  <PermissionProtectedRoute permission="credit.application.view">
                    <Layout currentPageName="CreditApplicationList">
                      <CreditApplicationList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />

              {/* Archive Routes */}
              <Route
                path="/archivedashboard"
                element={
                  <ProtectedRoute>
                    <Layout currentPageName="ArchiveDashboard">
                      <ArchiveDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/archive/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout currentPageName="ArchiveDashboard">
                      <ArchiveDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/archivecategorylist"
                element={
                  <PermissionProtectedRoute permission="archive.category.view">
                    <Layout currentPageName="ArchiveCategoryList">
                      <ArchiveCategoryList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/archive/categories"
                element={
                  <PermissionProtectedRoute permission="archive.category.view">
                    <Layout currentPageName="ArchiveCategoryList">
                      <ArchiveCategoryList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/documenttypelist"
                element={
                  <PermissionProtectedRoute permission="archive.general.view">
                    <Layout currentPageName="DocumentTypeList">
                      <DocumentTypeList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/archive/document-types"
                element={
                  <PermissionProtectedRoute permission="archive.general.view">
                    <Layout currentPageName="DocumentTypeList">
                      <DocumentTypeList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/documentlist"
                element={
                  <PermissionProtectedRoute permission="archive.general.view">
                    <Layout currentPageName="DocumentList">
                      <DocumentList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/archive/documents"
                element={
                  <PermissionProtectedRoute permission="archive.general.view">
                    <Layout currentPageName="DocumentList">
                      <DocumentList />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/documentvalidationworkflow"
                element={
                  <PermissionProtectedRoute permission="archive.search.view">
                    <Layout currentPageName="DocumentValidationWorkflow">
                      <DocumentValidationWorkflow />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/archive/validation"
                element={
                  <PermissionProtectedRoute permission="archive.search.view">
                    <Layout currentPageName="DocumentValidationWorkflow">
                      <DocumentValidationWorkflow />
                    </Layout>
                  </PermissionProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Layout currentPageName="Profile">
                      <Profile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout currentPageName="Settings">
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
